import React, { createContext, useState, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
  role?: string;
  client_id?: string;
  email?: string;
  [key: string]: any;
}

interface AuthContextType {
  isAdmin: boolean;
  isAuthenticated: boolean;
  clientId: string | null;
  adminEmail: string | null;
  handleLogin: (token: string) => void;
  handleLogout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  isAdmin: false,
  isAuthenticated: false,
  clientId: null,
  adminEmail: null,
  handleLogin: () => {},
  handleLogout: () => {},
  loading: true,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true); // <- new
  const [clientId, setClientId] = useState<string | null>(null);
  const [adminEmail, setAdminEmail] = useState<string | null>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        const decoded: DecodedToken = jwtDecode(storedToken);
        setIsAuthenticated(true);
        setIsAdmin(decoded.role === 'admin');
        setClientId(decoded.client_id || null);
        setAdminEmail(decoded.email || null);
      } catch {
        localStorage.removeItem('token');
      }
    }
    setLoading(false); // <- done checking
  }, []);

  const handleLogin = (token: string) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
    try {
      const decoded: DecodedToken = jwtDecode(token);
      setIsAdmin(decoded.role === 'admin');
      setClientId(decoded.client_id || null);
      setAdminEmail(decoded.email || null);
    } catch {
      setIsAdmin(false);
      setClientId(null);
      setAdminEmail(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setIsAdmin(false);
    setClientId(null);
    setAdminEmail(null);
  };

  const value: AuthContextType = {
    isAdmin,
    isAuthenticated,
    clientId,
    adminEmail,
    handleLogin,
    handleLogout,
    loading,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};

export function useAuth() {
  return useContext(AuthContext);
}
